import type { FieldPathByValue, FieldPathValue, FieldValues, UseFormReturn } from 'react-hook-form';

import { cn } from '@repo/lib/cn';
import { APIProvider, Map } from '@vis.gl/react-google-maps';
import { ClipboardPasteIcon, CompassIcon, LocateFixedIcon, MapPinIcon } from 'lucide-react';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Button, ButtonGroup } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { CenterOnMarkerControl, DndAdvancedMarker, MapAutocomplete, MapCameraToPlaceEffect, MapLocateControl, MapUserPositionMarker, ReverseGeocodeEffect } from '@/components/ui/google-map';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group';
import { useTheme } from '@/context/theme';

/**
 * Props for the LocationFormFieldset component.
 * @template TFieldValues - Form field values type.
 * @template TName - Field name for coordinates.
 * @template TAddressName - Field name for address (optional).
 * @property {UseFormReturn<TFieldValues>} form - The form instance.
 * @property {TName} coordinatesName - The name of the coordinates field.
 * @property {TAddressName} [addressName] - The name of the address field (optional).
 */
interface LocationFormFieldsetProps<
  TFieldValues extends FieldValues,
  TName extends FieldPathByValue<TFieldValues, string>,
  TAddressName extends FieldPathByValue<TFieldValues, string | null> = never,
> {
  form: UseFormReturn<TFieldValues>;
  coordinatesName: TName;
  addressName?: TAddressName;
}

export default function LocationFormFieldset<
  TFieldValues extends FieldValues,
  TName extends FieldPathByValue<TFieldValues, string>,
  TAddressName extends FieldPathByValue<TFieldValues, string | null> = never,
>(locationFormFieldsetProps: LocationFormFieldsetProps<TFieldValues, TName, TAddressName>) {
  const { form, coordinatesName, addressName } = locationFormFieldsetProps;

  const theme = useTheme();
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.Place | null>(null);
  const [userLocation, setUserLocation] = useState<google.maps.LatLngLiteral | null>(null);

  const markerPosition = useWatch({
    control: form.control,
    name: coordinatesName,
    compute: (value) => {
      if (typeof value !== 'string') return null;
      const [lat, lng] = value.split(',').map(parseFloat);
      return !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null;
    },
  });

  const address = useWatch({
    control: form.control,
    name: addressName as TAddressName,
  });

  const updateFormFields = (coordinates: google.maps.LatLngLiteral, address: string | null) => {
    form.setValue(coordinatesName, `${coordinates.lat},${coordinates.lng}` as FieldPathValue<TFieldValues, TName>, { shouldValidate: true, shouldDirty: true });

    if (addressName && address) {
      form.setValue(addressName, address as FieldPathValue<TFieldValues, TAddressName>, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleClipboardPaste = () => {
    navigator.clipboard.readText().then((text) => {
      form.setValue(coordinatesName, text as FieldPathValue<TFieldValues, TName>);
    });
  };

  const handleSelectLocation = (location: google.maps.LatLngLiteral | null) => {
    if (!location) return;
    updateFormFields(location, null);
  };

  const handleSelectedPlace = (place: google.maps.places.Place | null) => {
    if (!place?.location) return;

    const coordinates = place.location.toJSON();
    const placeAddress = place.formattedAddress ?? place.displayName ?? null;

    updateFormFields(coordinates, placeAddress);
    setSelectedPlace(place);
  };

  const handleReverseGeocodeAddress = (address: string | null) => {
    if (!addressName || !address) return;

    form.setValue(addressName, address as FieldPathValue<TFieldValues, TAddressName>, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <FormField
      name={coordinatesName}
      control={form.control}
      render={({ field }) => (
        <FormItem className={'col-span-1'}>
          <FormLabel>Lokalizacja</FormLabel>
          <APIProvider apiKey={import.meta.env.VITE_GOOGLE_API_KEY} language={'pl'}>
            <MapAutocomplete onPlaceSelect={handleSelectedPlace} />
            <div className="relative h-60 min-h-60 overflow-hidden rounded-sm border">
              <Map
                colorScheme={theme.googleMapsTheme}
                mapId={'49ae42fed52588c3'}
                style={{
                  width: '100%',
                  height: 'calc(100% - var(--spacing)*10)',
                }}
                defaultZoom={markerPosition ? 15 : 6}
                defaultCenter={markerPosition ? markerPosition : { lat: 52.0693, lng: 19.4803 }}
                gestureHandling={'cooperative'}
                disableDefaultUI={true}
                clickableIcons={false}
              >
                <ButtonGroup className="absolute top-2 right-2 z-10">
                  <Button variant="outline" asChild type="button">
                    <MapLocateControl onLocated={(pos) => setUserLocation(pos)} variant={'outline'}>
                      <LocateFixedIcon />
                    </MapLocateControl>
                  </Button>
                  <Button variant="outline" asChild type="button">
                    <CenterOnMarkerControl position={markerPosition} variant={'outline'}>
                      <CompassIcon />
                    </CenterOnMarkerControl>
                  </Button>
                </ButtonGroup>
                <MapCameraToPlaceEffect place={selectedPlace} />
                <ReverseGeocodeEffect position={markerPosition} setAddress={handleReverseGeocodeAddress} />
                <DndAdvancedMarker position={markerPosition} onSetPosition={handleSelectLocation} />
                <MapUserPositionMarker position={userLocation} onClick={() => handleSelectLocation(userLocation)} clickable={false} />
              </Map>
              <div className={cn('text-accent-foreground flex h-10 items-center gap-2 border-t p-2 text-sm', !address && 'hidden')}>
                <MapPinIcon className="text-muted-foreground size-4" />
                <span className="truncate">{address}</span>
              </div>
            </div>
          </APIProvider>
          <FormControl>
            <InputGroup>
              <InputGroupInput {...field} placeholder={'Podaj lokalizację'} />
              <InputGroupAddon align="inline-end">
                <InputGroupButton type="button" onClick={handleClipboardPaste}>
                  <ClipboardPasteIcon />
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
